import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  BrandType,
  BrandWorkingHoursType,
  CompanyType,
  CreateBrandFormType,
  PolygonType
} from 'utils/types';
import { days } from 'utils/constants';
import { BrandContext } from './context';
import { MetadataItemType } from 'pages/metadata/types';
import moment from 'moment';
import { ENUM_BRAND_TYPE } from 'utils/enum';
import { createTimeSelector, handleApiError } from 'utils/functions';
import { apiService } from 'services/base.service';
import useDashboardContext from 'contexts/dashboard.context';

const BrandProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [activeBrand, setActiveBrand] = useState({} as BrandType);
  const daysArray = days().map((item) => {
    return {
      name: item.name,
      opening_time: moment(item.opening_time, 'HH:mm:ss').format('HH:mm'),
      closing_time: moment(item.closing_time, 'HH:mm:ss').format('HH:mm'),
      active: true
    };
  });
  const [brandWorkingHours, setBrandWorkingHours] = useState<BrandWorkingHoursType[]>([]);
  const [createBrandForm, setCreateBrandForm] = useState({
    brand_keywords: [] as MetadataItemType[],
    brand_business_metadatas: [] as MetadataItemType[],
    brand_descriptive_metadatas: [] as MetadataItemType[],
    working_hours: brandWorkingHours.length ? brandWorkingHours : daysArray
  } as CreateBrandFormType);
  const [companies, setCompanies] = useState<CompanyType[]>([]);
  const [loadingCompanyOptions, setLoadingCompanyOptions] = useState(false);
  const [loadingPolygons, setLoadingPolygons] = useState(false);
  const [availablePolygons, setAvailablePolygons] = useState<PolygonType[]>([]);

  const { activeCokitchen } = useDashboardContext();

  const companyOptions = useMemo(() => {
    return companies.map((c) => ({ value: c.id, text: c.name }));
  }, [companies]);

  const loadCompanies = async () => {
    try {
      setLoadingCompanyOptions(true);
      const params = { status: 'verified', page: 1, size: Number.MAX_SAFE_INTEGER };
      const res = await apiService.get('company/all', {
        params,
        baseURL: process.env.REACT_APP_FCB_API_URL
      });
      setCompanies(res.data.data.results);
    } catch (err) {
      setCompanies([]);
    } finally {
      setLoadingCompanyOptions(false);
    }
  };

  const fetchPolygons = async () => {
    if (!activeCokitchen.cokitchen_id) return;
    try {
      setLoadingPolygons(true);
      const res = await apiService.get(
        `internal/cokitchen/polygons/${activeCokitchen.cokitchen_id}`,
        {
          isInternal: true
        }
      );

      const polygons: PolygonType[] = res.data.data || [];
      polygons.map((p) => ({
        ...p,
        available: true
      }));
      setAvailablePolygons(polygons);
    } catch (err: any) {
      handleApiError(err);
    } finally {
      setLoadingPolygons(false);
    }
  };

  const formatFormData = () => {
    const data: CreateBrandFormType = {
      name: activeBrand?.name,
      summary: activeBrand?.summary ?? '',
      description: activeBrand?.description ?? '',
      slug: activeBrand?.slug ?? '',
      new: activeBrand?.new ?? false,
      top: activeBrand?.top ?? false,
      pop_up: activeBrand?.pop_up ?? false,
      popup_color: activeBrand?.popup_color ?? '',
      coming_soon: activeBrand?.coming_soon ?? false,
      coming_soon_text: activeBrand?.coming_soon_text ?? '',
      logo: activeBrand?.logo ?? '',
      is_private: activeBrand?.is_private ?? false,
      brand_type: activeBrand.brand_type || ENUM_BRAND_TYPE.FOODCOURT,
      cutoff_time: activeBrand.cutoff_time ? createTimeSelector(activeBrand.cutoff_time) : null,
      images: activeBrand?.images ?? [],
      working_hours: brandWorkingHours.length ? brandWorkingHours : daysArray,
      brand_keywords: activeBrand?.brand_keywords,
      brand_business_metadatas: activeBrand?.brand_business_metadatas,
      brand_descriptive_metadatas: activeBrand?.brand_descriptive_metadatas,
      short_text: activeBrand?.short_text ?? '',
      live: activeBrand?.live,
      preorder: activeBrand?.preorder,
      preorder_duration_in_hours: activeBrand?.preorder_duration_in_hours,
      all_company_available: Boolean(activeBrand?.all_company_available),
      company_ids: activeBrand?.company_brands?.map((b) => b.company.id) ?? [],
      brand_partnership_id: activeBrand?.brand_partnership_id,
      polygon: (activeBrand?.polygon ?? []).map((p) => ({
        ...p,
        available: true
      }))
    };
    setCreateBrandForm(data);
  };

  useEffect(() => {
    loadCompanies();
    fetchPolygons();
  }, []);

  useEffect(() => {
    formatFormData();
  }, [activeBrand, brandWorkingHours]);

  return (
    <BrandContext.Provider
      value={{
        activeBrand,
        setActiveBrand,
        createBrandForm,
        setCreateBrandForm,
        brandWorkingHours,
        setBrandWorkingHours,
        companyOptions,
        loadingCompanyOptions,
        availablePolygons,
        loadingPolygons
      }}>
      {children}
    </BrandContext.Provider>
  );
};

export default BrandProvider;
